import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Phase, PhaseModule } from '../types';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

const statusColors: Record<string, string> = {
  pending: '#94a3b8',
  assigned: '#f59e0b',
  in_progress: '#3b82f6',
  completed: '#22c55e',
  failed: '#ef4444',
};

const statusLabels: Record<string, string> = {
  pending: 'Ожидает',
  assigned: 'Назначена',
  in_progress: 'В работе',
  completed: 'Готово',
  failed: 'Ошибка',
};

interface Props {
  onPhaseUploaded?: () => void;
  // Fires after a successful upload with the raw .md we already read
  // client-side for the POST — lets the parent fan the same content
  // out to multiple repos without hitting the server again.
  onPhaseContent?: (phase: Phase, content: string) => void;
}

export const PhaseManager: React.FC<Props> = ({ onPhaseUploaded, onPhaseContent }) => {
  const [phases, setPhases] = useState<Phase[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleUpload = useCallback(async (file: File) => {
    if (!file.name.endsWith('.md')) return;
    setUploading(true);
    try {
      const text = await file.text();
      const res = await fetch(`${API}/api/phases/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: file.name, content: text }),
      });
      if (res.ok) {
        const phase = await res.json();
        setPhases((prev) => [...prev, phase]);
        onPhaseUploaded?.();
        onPhaseContent?.(phase, text);
      }
    } catch (err) {
      console.error('Upload failed:', err);
    }
    setUploading(false);
  }, [onPhaseUploaded, onPhaseContent]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  }, [handleUpload]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  }, [handleUpload]);

  const assignModule = async (phaseId: string, moduleId: string) => {
    try {
      await fetch(`${API}/api/phases/${phaseId}/assign-module`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ moduleId, workerId: 'claude-coder' }),
      });
      setPhases((prev) =>
        prev.map((p) =>
          p.id === phaseId
            ? { ...p, modules: p.modules.map((m) => m.id === moduleId ? { ...m, status: 'assigned' as const, assignedTo: 'Claude' } : m) }
            : p
        )
      );
    } catch (err) {
      console.error('Assign failed:', err);
    }
  };

  const assignAll = async (phaseId: string) => {
    try {
      await fetch(`${API}/api/phases/${phaseId}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workerId: 'claude-coder' }),
      });
      setPhases((prev) =>
        prev.map((p) =>
          p.id === phaseId
            ? { ...p, modules: p.modules.map((m) => m.status === 'pending' ? { ...m, status: 'assigned' as const, assignedTo: 'Claude' } : m) }
            : p
        )
      );
    } catch (err) {
      console.error('Assign all failed:', err);
    }
  };

  // Hard-delete a phase (and its modules) from the dashboard.
  // Refused by the backend if the phase is currently in_progress
  // / review — surface that as a friendly alert.
  const deletePhase = async (phaseId: string, phaseName: string) => {
    if (!window.confirm(`Удалить "${phaseName}" целиком? Все модули и история по этой фазе пропадут.`)) return;
    try {
      const r = await fetch(`${API}/api/phases/${phaseId}`, { method: 'DELETE' });
      if (r.ok) {
        setPhases((prev) => prev.filter((p) => p.id !== phaseId));
      } else {
        const data = await r.json().catch(() => ({}));
        alert(`Не удалось удалить: ${data.message || data.error || `HTTP ${r.status}`}`);
      }
    } catch (err: any) {
      alert(`Сетевая ошибка: ${err.message || 'unknown'}`);
    }
  };

  // Reset modules stuck in non-terminal states back to pending.
  // Used after Stop / cancel — Pipeline keeps showing assigned /
  // in_progress until you reset, because module status is decoupled
  // from the actual claude process lifecycle.
  const resetPhase = async (phaseId: string) => {
    if (!window.confirm('Сбросить все незавершённые модули в pending? Готовые модули не тронем.')) return;
    try {
      const r = await fetch(`${API}/api/phases/${phaseId}/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (r.ok) {
        setPhases((prev) =>
          prev.map((p) =>
            p.id === phaseId
              ? {
                  ...p,
                  modules: p.modules.map((m) =>
                    ['assigned', 'in_progress', 'self_check', 'needs_fix'].includes(m.status)
                      ? { ...m, status: 'pending' as const, assignedTo: undefined, progress: 0 }
                      : m,
                  ),
                }
              : p,
          ),
        );
      }
    } catch (err) {
      console.error('Reset failed:', err);
    }
  };

  return (
    <div className="phase-manager">
      {/* Upload zone */}
      <div
        className={`upload-zone ${dragActive ? 'upload-active' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
      >
        <div className="upload-content">
          <span className="upload-icon">{uploading ? '...' : '+'}</span>
          <p>{uploading ? 'Загрузка...' : 'Перетащи .md файл фазы сюда'}</p>
          <label className="upload-btn">
            Выбрать файл
            <input type="file" accept=".md" onChange={handleFileInput} hidden />
          </label>
        </div>
      </div>

      {/* Phases list */}
      {phases.map((phase) => (
        <motion.div
          key={phase.id}
          className="phase-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="phase-header">
            <div>
              <h3>{phase.name}</h3>
              <span className="phase-file">{phase.fileName}</span>
            </div>
            <div className="phase-header-actions">
              <button className="btn-secondary phase-reset" onClick={() => resetPhase(phase.id)}>
                ↺ Reset
              </button>
              <button className="phase-delete" onClick={() => deletePhase(phase.id, phase.name)} title="Удалить фазу целиком">
                🗑
              </button>
              <button className="btn-assign-all" onClick={() => assignAll(phase.id)}>
                Назначить все Claude
              </button>
            </div>
          </div>

          <div className="modules-table">
            <div className="modules-header">
              <span>Модуль</span>
              <span>Статус</span>
              <span>Исполнитель</span>
              <span>Прогресс</span>
              <span></span>
            </div>
            {phase.modules.map((mod: PhaseModule, i: number) => (
              <motion.div
                key={mod.id}
                className="module-row"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.03 }}
              >
                <span className="module-name">{mod.name}</span>
                <span className="module-status" style={{ color: statusColors[mod.status] }}>
                  {statusLabels[mod.status]}
                </span>
                <span className="module-worker">{mod.assignedTo || '-'}</span>
                <div className="module-progress">
                  <div className="worker-progress-bar">
                    <div className="worker-progress-fill" style={{ width: `${mod.progress}%`, background: statusColors[mod.status] }} />
                  </div>
                  <span>{mod.progress}%</span>
                </div>
                <span>
                  {mod.status === 'pending' && (
                    <button className="btn-assign" onClick={() => assignModule(phase.id, mod.id)}>
                      Assign
                    </button>
                  )}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      ))}

      {phases.length === 0 && (
        <div className="empty-state">
          Загрузите .md файл фазы для начала работы
        </div>
      )}
    </div>
  );
};
